import { createSlice } from "@reduxjs/toolkit";
import { Toastify } from "../../../components/Toaster";
import {
  addingSideEffects,
  defaultErrorSideEffects,
  defaultLoadingSideEffects,
  defaultSuccessSideEffects,
  deletingSideEffects,
  errorSideEffects,
  loadingSideEffects,
  successSideEffects,
  updatingSideEffects
} from "..";

export interface RoleState {
  Data: Role[];
  isLoading: boolean;
  DefaultRole: string;
  isDefaultLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isAdding: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}
export interface Permission {
  Id: string;
  Name: string;
  OriginalName?: string;
  Description: string;
  ResourceId?: string;
  CreatedDate: Date;
  ModifiedDate: Date;
}

export interface Role {
  Id: string;
  Name: string;
  OriginalName?: string;
  Description: string;
  Level: string;
  OrgId: string;
  Default: boolean;
  Permissions: Permission[];
  CreatedDate: Date;
  ModifiedDate: Date;
}

const initialState: RoleState = {
  Data: [],
  DefaultRole: "",
  isLoading: false,

  isDefaultLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

// Slice
export const OrgRoleSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    getRolesRequest(state, _) {
      Object.assign(state, loadingSideEffects);
    },
    getRolesSuccess(state, action) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload.Data;
      state.DefaultRole = "";
    },
    getRolesError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    getRoleByIdRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getRoleByIdSuccess(state, action) {
      Object.assign(state, successSideEffects);
      const index = state.Data.findIndex((role) => role.Id === action.payload.Data.ID);
      if (index !== -1) {
        state.Data[index] = action.payload;
      }
    },
    getRoleByIdError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    createRoleRequest(state, _) {
      Object.assign(state, addingSideEffects);
    },
    createRoleSuccess(state, action) {
      Object.assign(state, successSideEffects);
      state.Data.push(action.payload);

      Toastify("success", "Role created successfully");
    },
    createRoleError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    updateRoleRequest(state, _) {
      Object.assign(state, updatingSideEffects);
    },
    updateRoleSuccess(state, action) {
      Object.assign(state, successSideEffects);
      const index = state.Data.findIndex((role) => role.Id === action.payload.Id);
      if (index !== -1) {
        state.Data[index] = action.payload;
      }

      Toastify("success", "Role updated successfully");
    },
    updateRoleError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    makeRoleDefaultRequest(state, _) {
      Object.assign(state, defaultLoadingSideEffects);
    },
    makeRoleDefaultSuccess(state, action) {
      Object.assign(state, defaultSuccessSideEffects);
      state.DefaultRole = action.payload;

      Toastify("success", "Default Role updated successfully");
    },
    setRoleDefault(state, action) {
      // Object.assign(state, defaultSuccessSideEffects);
      state.DefaultRole = action.payload;
    },
    makeRoleDefaultError(state, action) {
      Object.assign(state, defaultErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteRoleRequest(state, _) {
      Object.assign(state, deletingSideEffects);
    },
    deleteRoleSuccess(state, action) {
      Object.assign(state, successSideEffects);
      state.Data = state.Data.filter((role) => role.Id !== action.payload);

      Toastify("success", "Role deleted successfully");
    },
    deleteRoleError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const TenantRolesSlice = createSlice({
  name: "tenantRoles",
  initialState: initialState,
  reducers: {
    getTenantRolesRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getTenantRolesSuccess(state, action) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload.Data;
      state.DefaultRole = "";
    },
    getTenantRolesError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    createTenantRoleRequest(state, _) {
      Object.assign(state, addingSideEffects);
    },
    createTenantRoleSuccess(state, action) {
      Object.assign(state, successSideEffects);
      state.Data.push(action.payload);
      Toastify("success", "Role created successfully");
    },
    createTenantRoleError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    makeTenantRoleDefaultRequest(state, _) {
      Object.assign(state, defaultLoadingSideEffects);
    },
    makeTenantRoleDefaultSuccess(state, action) {
      Object.assign(state, defaultSuccessSideEffects);
      state.DefaultRole = action.payload;
      Toastify("success", "Default Role updated successfully");
    },
    makeTenantRoleDefaultError(state, action) {
      Object.assign(state, defaultErrorSideEffects);
      state.errorMessage = action.payload;
    },
    getTenantRoleDefaultRequest(state) {
      Object.assign(state, defaultLoadingSideEffects);
    },
    getTenantRoleDefaultSuccess(state, action) {
      Object.assign(state, defaultSuccessSideEffects);
      state.DefaultRole = action.payload;
    },
    getTenantRoleDefaultError(state, action) {
      Object.assign(state, defaultErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(OrgRoleSlice.actions.updateRoleRequest, (state) => {
      state.isUpdating = true;
      state.isError = false;
      state.isSuccess = false;

      state.errorMessage = null;
    });
    builder.addCase(OrgRoleSlice.actions.updateRoleError, (state, action) => {
      state.isUpdating = false;
      state.isError = true;
      state.isSuccess = false;
      state.errorMessage = action.payload;
    });
    builder.addCase(OrgRoleSlice.actions.updateRoleSuccess, (state, action) => {
      const index = state.Data.findIndex((role) => role.Id === action.payload.Id);
      if (index !== -1) {
        state.Data[index] = action.payload;
      }
      state.isUpdating = false;
      state.isSuccess = true;
    });
    builder.addCase(OrgRoleSlice.actions.deleteRoleRequest, (state) => {
      state.isDeleting = true;
      state.isError = false;
      state.isSuccess = false;

      state.errorMessage = null;
    });
    builder.addCase(OrgRoleSlice.actions.deleteRoleError, (state, action) => {
      state.isDeleting = false;
      state.isError = true;
      state.isSuccess = false;
      state.errorMessage = action.payload;
    });
    builder.addCase(OrgRoleSlice.actions.deleteRoleSuccess, (state, action) => {
      state.Data = state.Data.filter((role) => {
        return role.Id !== action.payload;
      });
      state.isDeleting = false;
      state.isSuccess = true;
    });
  }
});

export const {
  getTenantRolesRequest,
  getTenantRolesSuccess,
  getTenantRolesError,
  createTenantRoleRequest,
  createTenantRoleSuccess,
  createTenantRoleError,
  makeTenantRoleDefaultRequest,
  makeTenantRoleDefaultSuccess,
  makeTenantRoleDefaultError,
  getTenantRoleDefaultRequest,
  getTenantRoleDefaultSuccess,
  getTenantRoleDefaultError
} = TenantRolesSlice.actions;

export const {
  getRolesRequest,
  getRolesSuccess,
  getRolesError,
  getRoleByIdRequest,
  getRoleByIdSuccess,
  getRoleByIdError,
  createRoleRequest,
  createRoleSuccess,
  createRoleError,
  updateRoleRequest,
  updateRoleSuccess,
  updateRoleError,
  makeRoleDefaultRequest,
  makeRoleDefaultSuccess,
  setRoleDefault,
  makeRoleDefaultError,
  deleteRoleRequest,
  deleteRoleSuccess,
  deleteRoleError
} = OrgRoleSlice.actions;
